/**
 * MER-625: Operations is the drill layer for the Overview.
 *
 * Chris, Delta review 2026-09-02: "when I see operations ... this is where
 * some of the overview bulk gets broken down, like the delivery and stuff."
 * Repeated 2026-09-11. The Overview is the summary page; the detail lives on
 * the tab that owns the number. Clicking an Overview tile should land the
 * reader on that tab, on that tile.
 *
 * WHAT A DRILL IS HERE
 * A drill is NAVIGATION, never a second computation. Following `drill_to`
 * moves the reader to another tab with the page's filter state carried over
 * in the URL, and the target tile runs its own manifest binding exactly as it
 * always did. Nothing is recomputed, re-aggregated or re-ratio'd on the way,
 * so MER-715 (every on-screen ratio is SUM/SUM over one population) cannot be
 * violated by a drill: there is no new expression to get wrong.
 *
 * WHY THE SET IS SMALL
 * A drill is a claim: "the number you just read is broken down over there."
 * That claim only holds when both tiles compute the same measure over the
 * same population. So `drill_to` may only be authored between two widgets
 * whose manifest data binding (source, field, filter, timeline, ...) is
 * IDENTICAL, which scripts/drill-target.test.mjs asserts per template and per
 * resolved tenant manifest. Three Overview tiles qualify today. A tile that
 * merely feels related (RO Deliveries and Haul Count are both roll-off work,
 * and are not the same measure) gets no drill, because sending a reader from
 * 154 to 1,643 and calling it a breakdown is worse than no drill at all.
 *
 * WHAT THIS MODULE DOES NOT DECIDE
 * Whether the SOURCE tile is clickable at all. WidgetRenderer already drops
 * the click affordance from gated and empty tiles (MER-571: a tile withholding
 * its number must not look like a door), and this module is only reached from
 * that same click path.
 *
 * Plain .mjs so the node:test suite imports it without a bundler (the
 * navSections.mjs / priorWindow.mjs precedent).
 */

/** URL search param naming the widget the target tab should scroll to and ring. */
export const DRILL_FOCUS_PARAM = "focus";

/**
 * Tier ordering for the tab lock.
 *
 * MER-625: this moved out of CustomerDashboard.tsx so the drill gate and the
 * lock page read the same rule. A drill that opened a tier-locked tab would
 * land the reader on the upgrade notice with no number on it, which is a
 * broken promise rather than a locked feature.
 */
// Ranks start at 1: tabIsUnlocked treats a falsy rank as an unknown tier.
const TIER_RANK = { essential: 1, growth: 2, professional: 3, enterprise: 4 };

/**
 * Is this tab unlocked for the customer's tier? A tab with no declared tier,
 * or a manifest with no tier, is open (the pre-tier default).
 *
 * @param {{tier?: string} | undefined | null} tab
 * @param {string | undefined | null} customerTier
 * @returns {boolean}
 */
export function tabIsUnlocked(tab, customerTier) {
  return tierAtLeast(customerTier, tab?.tier);
}

/**
 * Does the customer's tier include the `want` plan? No `want`, or a manifest
 * with no tier, is open (the pre-tier default), as for tabs. #1084: also the
 * gate for a feature inside a tab, such as AR Aging's collections tools.
 *
 * @param {string | undefined | null} customerTier
 * @param {string | undefined | null} want
 * @returns {boolean}
 */
export function tierAtLeast(customerTier, want) {
  if (!want || !customerTier) return true;
  const need = TIER_RANK[want];
  const have = TIER_RANK[customerTier];
  // An unrecognised tier on either side is not a licence to open it.
  if (!need || !have) return false;
  return have >= need;
}

function trimmed(value) {
  return typeof value === "string" ? value.trim() : "";
}

/**
 * Resolve a widget's drill target against the manifest it renders in.
 *
 * Fails CLOSED: every unmet condition returns null and the caller falls back
 * to the widget's own behaviour (the row-level DrillDownModal). A drill that
 * cannot be honoured must never be advertised, because the reader learns
 * nothing from a click that lands on an empty tab.
 *
 * @param {{tabs?: object[], widgets?: object[], tier?: string, enabled_services?: string[]}} manifest
 * @param {{id?: string, tab?: string, drill_to?: string}} widget
 * @param {{isStaff?: boolean}} [opts] isStaff: a staff session may open
 *   staff_only tabs. Only a strict boolean true counts (the navSections.mjs
 *   rule), so an undefined session is treated as a client.
 * @returns {{tab: string, widgetId: string, name: string} | null}
 */
export function resolveDrillTarget(manifest, widget, opts = {}) {
  const wantedId = trimmed(widget?.drill_to);
  if (!wantedId) return null;
  if (!manifest || typeof manifest !== "object") return null;

  const widgets = Array.isArray(manifest.widgets) ? manifest.widgets : [];
  const target = widgets.find((w) => w && String(w.id) === wantedId);
  if (!target) return null;

  const targetTab = trimmed(target.tab);
  if (!targetTab) return null;
  // A drill into the tile's own tab is not a drill. It would replace the
  // row-level modal with a no-op scroll.
  if (targetTab === trimmed(widget.tab)) return null;

  // A tile that is not showing a number is not a detail layer. `blocked`
  // covers both our unbuilt work and MER-717's awaiting_feed tiles; neither
  // breaks anything down today.
  const status = trimmed(target.status) || "ok";
  if (status !== "ok") return null;

  // MER-306: the target must be a line this customer actually runs. The
  // resolver already drops service-gated widgets, but a handwritten or
  // overridden manifest can still carry one.
  const gate = trimmed(target.service_gate);
  if (gate && !(manifest.enabled_services ?? []).includes(gate)) return null;

  // MER-577: the resolver drops tabs that emptied out. A target whose tab is
  // gone has nowhere to land.
  const tabs = Array.isArray(manifest.tabs) ? manifest.tabs : [];
  const tab = tabs.find((t) => t && (t.id ?? t.label) === targetTab);
  if (!tab) return null;

  // MER-688: a client session never opens a staff_only tab.
  if (tab.staff_only === true && opts.isStaff !== true) return null;

  // MER-159: a tier-locked tab shows the upgrade notice, not the detail.
  if (!tabIsUnlocked(tab, manifest.tier)) return null;

  return { tab: targetTab, widgetId: wantedId, name: trimmed(target.name) || wantedId };
}

/**
 * The URL a drill navigates to: the target tab under the same slug, the
 * page's current search params carried over verbatim, plus ?focus=<widget id>.
 *
 * Carrying the params over is the whole reason the two numbers agree. Period,
 * range, month, division and service chips all live in the URL
 * (globalFilters.ts), so the target tile opens under the same window and scope
 * the reader was just looking at. Dropping them would land an identical
 * binding on a different window and read as a mismatch.
 *
 * The path is built the way DashboardShell builds its nav links (raw slug and
 * tab id, no encodeURIComponent) so a drill and a sidebar click produce the
 * same location for the same tab, and NavLink's active state cannot disagree
 * with itself.
 *
 * @param {string} slug
 * @param {{tab: string, widgetId: string}} target
 * @param {URLSearchParams | string | null | undefined} params current page params
 * @returns {string}
 */
export function drillHref(slug, target, params) {
  const next = new URLSearchParams(params ?? undefined);
  next.set(DRILL_FOCUS_PARAM, target.widgetId);
  const qs = next.toString();
  return `/${slug}/${target.tab}${qs ? `?${qs}` : ""}`;
}
